import React from 'react';
import PropTypes from 'prop-types';
import {
  params, arrayOf, shape, string, json, useDataModel
} from '@thd-nucleus/data-sources';
import {
  useHelmet, TitleTag, MetaTag, LinkTag, ScriptTag
} from '@thd-nucleus/thd-helmet';

import { getStructuredBreadcrumbs } from '../helpers/clean-breadcrumb-data';
import { getStructuredFAQ } from './structured-data';
import {
  getStoreDetailsTitle,
  getStoreDetails,
  getCurrentPromoImageSrc,
  getPreloadImageTag
} from '../helpers/pps-helpers';
import { EMT_PAGE_NAMES, LAST_PATH, OPEN_GRAPH_FACEBOOK_ADMIN, PAGE_TYPE } from '../constants';

const RENTALS_TITLE_PREFIX = 'Tool and Truck Rental';

const getRentalsDescription = ({ storeName, city, state }) => {
  if (storeName && city && state) {
    return `Get the tool and truck you need at The Home Depot ${storeName} with Home Depot tool rental or`
      + ` Home Depot truck rental. Whatever the job, we have what you need in ${city}, ${state}`;
  }

  return 'The Home Depot';
};

const getMetadataValues = (data) => {
  const { emtData } = data;
  const emtContentMetadata = emtData?.emtContent?.content?.rentalsMetadata || {};
  const {
    storeName, city, canonicalURL, state, postalCode, storeServicesPhone
  } = getStoreDetails({ data: data.storeData, path: LAST_PATH.RENTALS });

  const title = emtContentMetadata?.title || getStoreDetailsTitle({
    titlePrefix: RENTALS_TITLE_PREFIX,
    storeName,
    city,
    postalCode
  });
  const metaDescription = emtContentMetadata?.metaDescription
    || getRentalsDescription({ storeName, city, state });
  const ogTitle = emtContentMetadata?.ogTitle || getStoreDetailsTitle({
    titlePrefix: RENTALS_TITLE_PREFIX,
    storeName,
    city,
    postalCode
  });
  const ogDescription = emtContentMetadata?.ogDescription || getRentalsDescription({ storeName, city, state });
  const ogType = emtContentMetadata?.ogType || 'PPS Rentals';
  const ogImage = emtContentMetadata?.ogImage;
  const schema = emtContentMetadata?.schema;

  return {
    canonicalURL,
    title,
    metaDescription,
    ogTitle,
    ogDescription,
    ogType,
    ogImage,
    schema,
  };
};

const rentalsHandler = ({ props }) => {
  const tags = [];

  const {
    title,
    metaDescription,
    ogDescription,
    canonicalURL,
    ogTitle,
    ogType,
    ogImage,
    schema,
  } = getMetadataValues(props);

  tags.push(new TitleTag(title));
  tags.push(new MetaTag({ name: 'description', content: metaDescription, id: 'prop-description' }));
  tags.push(new LinkTag({ rel: 'canonical', href: canonicalURL, id: 'canonical' }));
  tags.push(new MetaTag({ property: 'og:title', content: ogTitle, id: 'og-title' }));
  tags.push(new MetaTag({ property: 'og:description', content: ogDescription, id: 'og-description' }));
  if (ogImage) tags.push(new MetaTag({ property: 'og:image', content: ogImage, id: 'og-image' }));
  tags.push(new MetaTag({ property: 'og:url', content: canonicalURL, id: 'og-url' }));
  tags.push(new MetaTag({ property: 'og:type', content: ogType, id: 'ogType' }));
  tags.push(new MetaTag({ property: 'og:site_name', content: 'The Home Depot', id: 'og-site_name' }));
  tags.push(new MetaTag({ property: 'fb:admins', content: OPEN_GRAPH_FACEBOOK_ADMIN, id: 'fb-admins' }));
  tags.push(getPreloadImageTag('https://assets.homedepot-static.com/mcstore/images/rentals/hero-banner-2x.jpg'));

  // Check for an active promotion and preload the image for the banner
  const promotionImageUrl = getCurrentPromoImageSrc(
    props?.emtData?.emtContent?.content?.storeFinderPromotions,
    EMT_PAGE_NAMES.RENTALS
  );
  if (promotionImageUrl) {
    tags.push(getPreloadImageTag(promotionImageUrl));
  }

  if (schema) {
    tags.push(new ScriptTag({ content: schema, id: 'rentalsStructuredData' }));
  } else {
    const breadcrumbSchema = getStructuredBreadcrumbs({
      props,
      departmentName: 'Tool & Truck Rentals',
      departmentID: PAGE_TYPE.RENTALS
    });
    const faqPageSchema = getStructuredFAQ(props, 'rentals');

    tags.push(new ScriptTag({ content: breadcrumbSchema, id: 'rentalsStructuredBreadcrumb' }));
    tags.push(new ScriptTag({ content: faqPageSchema, id: 'rentalsStructuredFAQData' }));
  }

  return tags;
};

const RentalsMetadata = ({ storeId }) => {
  const options = {
    variables: {
      storeSearchInput: storeId,
      pagesize: '1'
    }
  };

  const faqOption = {
    variables: {
      storeId
    },
    skip: !storeId
  };

  const emtContentOptions = {
    variables: {
      contentId: storeId,
      pageName: EMT_PAGE_NAMES.RENTALS
    }
  };

  const { data: storeData, loading, error } = useDataModel('storeSearch', options);
  const { data: emtData, loading: emtLoading, error: emtError } = useDataModel('emtContent', emtContentOptions);

  const props = { storeData, emtData, storeId };
  useHelmet('rentalsMetadata', { props }, rentalsHandler, [storeId]);

  return null;
};

RentalsMetadata.displayName = 'RentalsMetadata';

RentalsMetadata.dataModel = {
  storeSearch: params({
    storeSearchInput: string().isRequired(),
    pagesize: string().isRequired()
  }).shape({
    stores: arrayOf(shape({
      address: shape({
        city: string(),
        state: string(),
        postalCode: string(),
        street: string()
      }),
      storeId: string(),
      name: string(),
      phone: string(),
      storeDetailsPageLink: string(),
      storeHours: shape({
        sunday: shape({
          open: string(),
          close: string()
        }),
        monday: shape({
          open: string(),
          close: string()
        })
      })
    })),
  }),
  emtContent: params({
    contentId: string().isRequired(),
    pageName: string().isRequired()
  }).shape({
    content: json()
  }),
};

RentalsMetadata.propTypes = {
  storeId: PropTypes.string
};

RentalsMetadata.defaultProps = {
  storeId: ''
};

export { RentalsMetadata };